import React from "react";
import { CirclePlus, Trash2, X } from "lucide-react";
import { v4 as uuidv4 } from "uuid";

import { buildPartyTypeOptions } from "../../../api/creditAccounts";
import { useAppSelector } from "../../../app/store";
import { getAccountProductInfo } from "../../creditAccounts/logic/slice";
import { getStakeholders } from "../../stakeholder/logic/stakeholderSlice";

import FormField, {
  useTouched,
} from "../../create-case/components/party-involved/FormField";

import MultiSelectSummaryDropdown from "../../../../src/components/ui/selectdropdown/v2/MultiSelectSummaryDropdown";
import { OptionNameValuePair } from "../../../lib/interfaces/types";
import ButtonV2 from "../../../components/ui/button/v2";
import { RELATED_AS_LABELS, RelatedAs } from "../../../api/stakeholder_relationships";
import { RelationConnectorSvg } from "../../create-case/components/party-involved/PartyRelationRow";

export interface RelationItem {
  id: string;
  related_as: RelatedAs | "";
  account_numbers: string[];
}

export interface PartyRelationship {
  id: string;
  name: string;
  relations: RelationItem[];
}

export const getPartyRelationshipErrors = (
  party: PartyRelationship,
  all: PartyRelationship[],
  existingStakeholders?: Array<{ name: string }>
) => {
  const relations: Record<string, string> = {};
  const account_numbers: Record<string, string> = {};
  let name: string | undefined;

  const normalizedPartyName = party.name.replace(/\s+/g, "").toLowerCase();

  if (!party.name.trim()) {
    name = "Party name is required.";
  } else if (
    all.filter(
      (p) => p.name.replace(/\s+/g, "").toLowerCase() === normalizedPartyName
    ).length > 1
  ) {
    name = "Party name must be unique.";
  } else if (
    existingStakeholders &&
    existingStakeholders.some(
      (s) => s.name?.replace(/\s+/g, "").toLowerCase() === normalizedPartyName
    )
  ) {
    name = "Party already exists in this case.";
  }

  party.relations.forEach((r) => {
    if (!r.related_as) relations[r.id] = "Party type is required.";
  });

  party.relations.forEach((r) => {
    if (!r.account_numbers || r.account_numbers.length === 0) {
      account_numbers[r.id] = "Atleast 1 account number is required.";
    }
  });

  party.relations.forEach((r) => {
    if (!r.related_as) return;
    const isDuplicate = party.relations.some(
      (other) =>
        other.id !== r.id &&
        other.related_as === r.related_as &&
        other.account_numbers.some((num) => r.account_numbers.includes(num))
    );
    if (isDuplicate) {
      const label = RELATED_AS_LABELS[r.related_as] || r.related_as;
      account_numbers[r.id] = `Account already added for ${label}`;
    }
  });

  return { name, relations, account_numbers };
};

export const isPartyRelationshipValid = (
  party: PartyRelationship,
  all: PartyRelationship[],
  existingStakeholders?: Array<{ name: string }>
) => {
  const { name, relations, account_numbers } = getPartyRelationshipErrors(
    party,
    all,
    existingStakeholders
  );
  return (
    !name &&
    Object.keys(relations).length === 0 &&
    Object.keys(account_numbers).length === 0 &&
    party.relations.length > 0
  );
};

interface PartyRelationRowProps {
  partyRelationship: PartyRelationship;
  allParties: PartyRelationship[];
  showErrors?: boolean;
  onChange: (patch: PartyRelationship) => void;
  onDelete?: () => void;
}

export const buildEmptyRelationItem = (): RelationItem => ({
  id: uuidv4(),
  related_as: "",
  account_numbers: [],
});

const PartyRelationRow: React.FC<PartyRelationRowProps> = ({
  partyRelationship,
  allParties,
  showErrors = false,
  onChange,
  onDelete,
}) => {
  const { isTouched, markTouched } = useTouched();
  const loanAccounts = useAppSelector(getAccountProductInfo);
  const existingStakeholders = useAppSelector(getStakeholders);
  const partyTypeOptions = buildPartyTypeOptions();

  const accountOptions: OptionNameValuePair<string>[] = loanAccounts.map(
    (account) => ({
      name: account.ext_account_number,
      value: account.loan_reference_id,
    })
  );

  const errors = getPartyRelationshipErrors(
    partyRelationship,
    allParties,
    existingStakeholders
  );

  const show = (field: string) => showErrors || isTouched(field);
  const hasNameError = Boolean(show("name") && errors.name);

  const handleAddRelation = () => {
    onChange({
      ...partyRelationship,
      relations: [...partyRelationship.relations, buildEmptyRelationItem()],
    });
  };

  const handleRemoveRelation = (relationId: string) => {
    onChange({
      ...partyRelationship,
      relations: partyRelationship.relations.filter((r) => r.id !== relationId),
    });
  };

  const handleChangeRelation = (
    relationId: string,
    patch: Partial<RelationItem>
  ) => {
    onChange({
      ...partyRelationship,
      relations: partyRelationship.relations.map((r) =>
        r.id === relationId ? { ...r, ...patch } : r
      ),
    });
  };

  return (
    <div className="flex flex-col gap-3 bg-white">
      {/* --- Main Party Name Row --- */}
      <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-[1fr_auto]">
        <FormField
          type="text"
          label="Party Name"
          required
          placeholder="Enter name"
          value={partyRelationship.name}
          error={show("name") ? errors.name : undefined}
          onChange={(value) =>
            onChange({
              ...partyRelationship,
              name: value,
            })
          }
          onBlur={() => markTouched("name")}
        />

        <div className="flex items-center pt-5">
          <ButtonV2
            variant="outline"
            size="sm"
            disabled={!onDelete}
            onClick={onDelete}
            title={!onDelete ? "Main party cannot be deleted" : "Delete party"}
            className="h-9 px-2 rounded-[8px] bg-white border border-[#E5E5E5] text-red-500 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] hover:bg-red-500 hover:text-white hover:border-red-500 disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-red-500 disabled:cursor-not-allowed"
            prefix={<Trash2 width="16" height="16" />}
          />
        </div>
      </div>

      <div className="relative flex flex-col gap-3 pl-[20px]">
        {partyRelationship.relations.map((relation, index) => {
          const isFirst = index === 0;
          const isLast = index === partyRelationship.relations.length - 1;

          const typeError = show(`type:${relation.id}`)
            ? errors.relations[relation.id]
            : undefined;

          const loanError =
            show(`account:${relation.id}`) || showErrors
              ? errors.account_numbers[relation.id]
              : undefined;

          const selectedAccounts = accountOptions.filter((option) =>
            relation.account_numbers.includes(option.value)
          );

          return (
            <div key={relation.id} className="relative w-full">
              {!isLast && (
                <div className="absolute left-[-20px] top-0 bottom-[-12px] w-[1px] bg-[#E5E5E5] pointer-events-none" />
              )}

              <RelationConnectorSvg
                isFirst={isFirst}
                hasParentError={isFirst && hasNameError}
              />

              <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-[1fr_1fr_auto]">
                <FormField
                  type="select"
                  label="Party Type"
                  required
                  placeholder="Select Party Type"
                  value={relation.related_as}
                  options={partyTypeOptions}
                  hideLabel={!isFirst}
                  error={typeError}
                  onChange={(value) =>
                    handleChangeRelation(relation.id, {
                      related_as: value as RelatedAs,
                    })
                  }
                  onBlur={() => markTouched(`type:${relation.id}`)}
                />

                <div className="flex min-w-0 flex-col gap-1">
                  {isFirst && (
                    <label className="text-xs font-medium text-gray-700">
                      Loan Accounts
                    </label>
                  )}

                  <div
                    className={`flex min-h-9 items-center rounded-[8px] border bg-white px-2.5 ${
                      loanError ? "border-red-500" : "border-[#E5E5E5]"
                    }`}
                  >
                    <MultiSelectSummaryDropdown
                      name={`accounts`}
                      placeholder="Select accounts"
                      value={relation.account_numbers}
                      options={accountOptions}
                      hideChevronWhenSelected={false}
                      onChange={(values) =>
                        handleChangeRelation(relation.id, {
                          account_numbers: values,
                        })
                      }
                      renderCustomView={() => {
                        if (selectedAccounts.length === 0) {
                          return (
                            <span
                              className={
                                loanError
                                  ? "text-sm text-red-500"
                                  : "text-sm text-[#737373]"
                              }
                            >
                              Select accounts
                            </span>
                          );
                        }

                        return (
                          <div className="flex flex-wrap gap-1">
                            {selectedAccounts.map((option) => (
                              <span
                                key={option.value}
                                className="inline-flex items-center gap-1 rounded-[6px] bg-[#EBEEFE] px-2 py-0.5 text-xs font-medium text-[#304ADD]"
                              >
                                {option.name}
                                <button
                                  type="button"
                                  className="hover:text-red-500"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleChangeRelation(relation.id, {
                                      account_numbers:
                                        relation.account_numbers.filter(
                                          (v) => v !== option.value
                                        ),
                                    });
                                  }}
                                >
                                  <X width={12} height={12} />
                                </button>
                              </span>
                            ))}
                          </div>
                        );
                      }}
                    />
                  </div>

                  {loanError && (
                    <span className="text-xs text-red-500">{loanError}</span>
                  )}
                </div>

                <div className={`flex items-center ${isFirst ? "pt-5" : ""}`}>
                  <ButtonV2
                    variant="outline"
                    size="sm"
                    disabled={partyRelationship.relations.length <= 1}
                    onClick={() => handleRemoveRelation(relation.id)}
                    title={
                      partyRelationship.relations.length <= 1
                        ? "At least one party type is required"
                        : "Remove party type"
                    }
                    className="h-9 px-2 rounded-[8px] bg-white border border-[#E5E5E5] text-red-500 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] hover:bg-red-500 hover:text-white hover:border-red-500 disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-red-500 disabled:cursor-not-allowed"
                    prefix={<Trash2 width="16" height="16" />}
                  />
                </div>
              </div>
            </div>
          );
        })}

        <button
          type="button"
          className="flex items-center gap-1 text-xs text-[#737373] font-medium self-start hover:text-[#3551F3]"
          onClick={handleAddRelation}
        >
          <CirclePlus width={14} height={14} /> Add another
        </button>

      </div>
    </div>
  );
};

export default PartyRelationRow;